import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Download, FileText, Upload, X, ChevronUp, ChevronDown, Eye, Trash2, Edit, Plus, RefreshCw, DollarSign, FileBarChart, Award, Building, Printer, Bell, CheckCircle, XCircle, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import MinimalDatePicker from '../components/MinimalDatePicker';
import AddExpenseDialog from '../components/AddExpenseDialog';
import api from '../utils/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const CATEGORIES = [
  // State Filing Categories
  { value: 'state_filing_fee', label: 'State Filing Fee' },
  { value: 'annual_report_fee', label: 'Annual Report Fee' },
  { value: 'foreign_qualification_fee', label: 'Foreign Qualification Fee' },
  { value: 'name_change_fee', label: 'Name Change Fee' },
  { value: 'good_standing_certificate_fee', label: 'Good Standing Certificate' },
  { value: 'reinstatement_fee', label: 'Reinstatement Fee' },
  { value: 'amendment_fee', label: 'Amendment Fee' },
  
  // Licensing & Renewal Categories
  { value: 'license_application_fee', label: 'License Application Fee' },
  { value: 'license_renewal_fee', label: 'License Renewal Fee' },
  { value: 'ancillary_registration_fee', label: 'Ancillary Registration Fee' },
  { value: 'fingerprinting_fee', label: 'Fingerprinting Fee' },
  { value: 'background_check_fee', label: 'Background Check Fee' },
  { value: 'regulatory_fee', label: 'Regulatory Fee' },
  { value: 'continuing_education_fee', label: 'Continuing Education Fee' },
  
  // DBA & Name Registration Categories
  { value: 'dba_registration_fee', label: 'DBA Registration Fee' },
  { value: 'dba_renewal_fee', label: 'DBA Renewal Fee' },
  { value: 'publication_fee', label: 'Publication Fee' },
  { value: 'trade_name_fee', label: 'Trade Name Fee' },
  { value: 'assumed_name_fee', label: 'Assumed Name Fee' },
  { value: 'fictitious_name_fee', label: 'Fictitious Name Fee' },
  
  // Other
  { value: 'legal_fee', label: 'Legal Fee' },
  { value: 'consulting_fee', label: 'Consulting Fee' },
  { value: 'other', label: 'Other' }
];

const STATUSES = ['paid', 'pending', 'overdue', 'reimbursed'];

const ExpenseTrackerEnhanced = ({ user }) => {
  // Data state
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 1
  });
  const [overviewStats, setOverviewStats] = useState({
    total_spend: 0,
    state_filing_fees: 0,
    licensing_fees: 0,
    dba_fees: 0,
    counts: { total: 0, state_filing: 0, licensing: 0, dba: 0 }
  });
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [dateRangeFilter, setDateRangeFilter] = useState('all'); // 'all', 'year', 'quarter', 'month'

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    states: [],
    category: 'all',
    status: 'all',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
    sort_by: 'created_at',  // Changed from payment_date to created_at for more reliable sorting
    sort_order: 'desc',
    page: 1,
    limit: 50
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [applyTrigger, setApplyTrigger] = useState(0); // Trigger for applying filters
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expensePrompts, setExpensePrompts] = useState([]);
  const [promptsExpanded, setPromptsExpanded] = useState(false);
  const [promptFromDraft, setPromptFromDraft] = useState(null);
  const fileInputRef = useRef(null);

  // Load expenses
  const loadExpenses = async () => {
    console.log('[ExpenseTracker] loadExpenses called with filters:', filters);
    setLoading(true);
    try {
      // Build query params
      const params = {
        page: filters.page,
        limit: filters.limit,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      };

      if (filters.search) params.search = filters.search;
      if (filters.states.length > 0) params.states = filters.states.join(',');
      if (filters.category && filters.category !== 'all') params.category = filters.category;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.min_amount) params.min_amount = filters.min_amount;
      if (filters.max_amount) params.max_amount = filters.max_amount;

      console.log('[ExpenseTracker] Calling API with params:', params);
      const data = await api.getExpenses(params);
      console.log('[ExpenseTracker] API returned:', data);
      
      setExpenses(data.expenses || []);
      setPagination(data.pagination || pagination);
      
      console.log('[ExpenseTracker] Set expenses state:', data.expenses?.length || 0, 'expenses');
    } catch (error) {
      console.error('[ExpenseTracker] Failed to load expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load overview stats - State Matrix Integrated
  const loadOverviewStats = async () => {
    setOverviewLoading(true);
    try {
      // Default to current calendar year for spend period
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;
      
      const data = await api.getExpenseTrackerOverviewCards({
        start_date: startDate,
        end_date: endDate
      });
      setOverviewStats(data);
      console.log('[ExpenseTracker] Loaded State Matrix-integrated overview cards:', data);
    } catch (error) {
      console.error('Failed to load overview stats:', error);
    } finally {
      setOverviewLoading(false);
    }
  };

  // Load expense prompts
  const loadExpensePrompts = async () => {
    try {
      const data = await api.getExpensePrompts('pending');
      setExpensePrompts(data.prompts || []);
    } catch (error) {
      console.error('Failed to load expense prompts:', error);
      setExpensePrompts([]);
    }
  };

  useEffect(() => {
    loadOverviewStats();
  }, [dateRangeFilter]); // Reload when date range changes
  
  useEffect(() => {
    console.log('[ExpenseTracker] useEffect triggered - loading expenses and prompts');
    loadExpenses();
    loadExpensePrompts();
  }, [filters.page, filters.limit, filters.sort_by, filters.sort_order, applyTrigger]);

  // Apply filters
  const applyFilters = () => {
    // Convert "all" values back to empty strings for API
    const apiFilters = { ...filters };
    if (apiFilters.category === 'all') apiFilters.category = '';
    if (apiFilters.status === 'all') apiFilters.status = '';
    
    setFilters(apiFilters);
    setApplyTrigger(prev => prev + 1); // Trigger re-fetch
  };

  // Helper to get date range label
  const getDateRangeLabel = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    
    switch(dateRangeFilter) {
      case 'year': return `${year} YTD`;
      case 'quarter': 
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `Q${quarter} ${year}`;
      case 'month': 
        return `${month}/${year}`;
      case 'all':
      default: return 'All Time';
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      states: [],
      category: 'all',
      status: 'all',
      start_date: '',
      end_date: '',
      min_amount: '',
      max_amount: '',
      sort_by: 'payment_date',
      sort_order: 'desc',
      page: 1,
      limit: 50
    });
    setTimeout(() => loadExpenses(), 100);
  };

  // Add/Edit expense handlers
  const handleAddExpense = () => {
    setEditingExpense(null);
    setPromptFromDraft(null);
    setAddExpenseOpen(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setPromptFromDraft(null);
    setAddExpenseOpen(true);
  };

  const handleCompletePrompt = (prompt) => {
    // Convert prompt to expense draft and open dialog in CREATE mode
    const expenseDraft = {
      title: prompt.suggested_title,
      category: prompt.suggested_category,
      amount: prompt.suggested_amount,
      state: prompt.suggested_state,
      vendor: prompt.suggested_vendor,
      entity_id: prompt.suggested_entity_id,
      license_id: prompt.suggested_license_id,
      dba_id: prompt.suggested_dba_id,
      filing_id: prompt.suggested_filing_id,
      task_id: prompt.task_id,
      description: prompt.notes,
      status: 'pending'
    };
    
    setPromptFromDraft(prompt);
    // Set editingExpense to pre-fill the form, but AddExpenseDialog needs to know this is CREATE mode
    setEditingExpense(expenseDraft);
    setAddExpenseOpen(true);
  };

  const handleDismissPrompt = async (promptId) => {
    try {
      await api.updateExpensePrompt(promptId, { status: 'dismissed' });
      loadExpensePrompts();
    } catch (error) {
      console.error('Failed to dismiss prompt:', error);
    }
  };

  const handleExpenseSuccess = async () => {
    console.log('[ExpenseTracker] handleExpenseSuccess called - reloading data');
    
    // Force reload by incrementing trigger
    setApplyTrigger(prev => prev + 1);
    
    // If this was from a prompt, mark it as completed
    if (promptFromDraft) {
      try {
        await api.updateExpensePrompt(promptFromDraft.prompt_id, { 
          status: 'completed'
        });
      } catch (error) {
        console.error('Failed to update prompt status:', error);
      }
    }
    
    // Close dialog and reset state
    setAddExpenseOpen(false);
    setEditingExpense(null);
    setPromptFromDraft(null);
    
    console.log('[ExpenseTracker] Triggered expense reload');
  };

  // Sort handler
  const handleSort = (column) => {
    if (filters.sort_by === column) {
      // Toggle order
      setFilters({
        ...filters,
        sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // New column
      setFilters({
        ...filters,
        sort_by: column,
        sort_order: 'desc'
      });
    }
  };

  // Invoice upload
  const handleInvoiceUpload = async (expenseId, file) => {
    setUploadingInvoice(expenseId);
    try {
      await api.uploadInvoice(expenseId, file);
      await loadExpenses();
      
      const { toast } = await import('sonner');
      toast.success('Invoice uploaded successfully');
    } catch (error) {
      console.error('Failed to upload invoice:', error);
      const { toast } = await import('sonner');
      toast.error(error.message || 'Failed to upload invoice');
    } finally {
      setUploadingInvoice(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Invoice delete
  const handleInvoiceDelete = async (expenseId) => {
    try {
      await api.deleteInvoice(expenseId);
      await loadExpenses();
      
      const { toast } = await import('sonner');
      toast.success('Invoice deleted successfully');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      const { toast } = await import('sonner');
      toast.error(error.message || 'Failed to delete invoice');
    }
  };

  // Export CSV
  const handleExport = () => {
    const params = {
      sort_by: filters.sort_by,
      sort_order: filters.sort_order
    };

    if (filters.search) params.search = filters.search;
    if (filters.states.length > 0) params.states = filters.states.join(',');
    if (filters.category && filters.category !== 'all') params.category = filters.category;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.min_amount) params.min_amount = filters.min_amount;
    if (filters.max_amount) params.max_amount = filters.max_amount;

    const exportUrl = api.exportExpensesCSV(params);
    window.open(exportUrl, '_blank');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date to mm/dd/yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Get status color with group-hover for table row hover
  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 group-hover:text-white group-hover:bg-emerald-600 transition-colors',
      pending: 'bg-yellow-100 text-yellow-800 group-hover:text-white group-hover:bg-yellow-600 transition-colors',
      overdue: 'bg-red-100 text-red-800 group-hover:text-white group-hover:bg-red-600 transition-colors',
      reimbursed: 'bg-blue-100 text-blue-800 group-hover:text-white group-hover:bg-blue-600 transition-colors'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 group-hover:text-white group-hover:bg-gray-600 transition-colors';
  };

  // State multi-select toggle
  const toggleState = (state) => {
    if (filters.states.includes(state)) {
      setFilters({ ...filters, states: filters.states.filter(s => s !== state) });
    } else {
      setFilters({ ...filters, states: [...filters.states, state] });
    }
  };

  // Sort indicator
  const SortIndicator = ({ column }) => {
    if (filters.sort_by !== column) return <ChevronUp className="h-3 w-3 opacity-0" />;
    return filters.sort_order === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-blue-600" />
      : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  return (
    <div className="space-y-5 p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="expense-tracker-enhanced">
      {/* Print Header - Hidden on screen, visible on print */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">North Bridge Solutions - Expense Report</h1>
        <p className="text-sm text-muted-foreground">Generated: {new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '/')}</p>
        {(filters.states.length > 0 || filters.category || filters.status || filters.start_date || filters.end_date) && (
          <div className="mt-2 text-xs">
            <strong>Applied Filters:</strong>
            {filters.states.length > 0 && <span className="ml-2">States: {filters.states.join(', ')}</span>}
            {filters.category && <span className="ml-2">Category: {filters.category}</span>}
            {filters.status && <span className="ml-2">Status: {filters.status}</span>}
            {filters.start_date && <span className="ml-2">From: {formatDate(filters.start_date)}</span>}
            {filters.end_date && <span className="ml-2">To: {formatDate(filters.end_date)}</span>}
          </div>
        )}
        <div className="mt-2 text-sm font-semibold">
          Total Records: {expenses.length} | Total Amount: {formatCurrency(expenses.reduce((sum, e) => sum + (e.amount || 0), 0))}
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="expense-tracker-title">
            Expense Tracker
          </h1>
          <p className="text-sm text-muted-foreground">
            Compliance spend control center • {getDateRangeLabel()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleAddExpense} data-testid="add-expense-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
          
          {/* Sort Dropdown */}
          <Select
            value={`${filters.sort_by}-${filters.sort_order}`}
            onValueChange={(value) => {
              const [sort_by, sort_order] = value.split('-');
              setFilters({ ...filters, sort_by, sort_order });
            }}
          >
            <SelectTrigger className="w-[180px]" data-testid="sort-select">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4" />
                <SelectValue placeholder="Sort..." />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at-desc">
                <div className="flex items-center gap-2">
                  <ArrowDown className="h-3 w-3" />
                  Created (Newest)
                </div>
              </SelectItem>
              <SelectItem value="created_at-asc">
                <div className="flex items-center gap-2">
                  <ArrowUp className="h-3 w-3" />
                  Created (Oldest)
                </div>
              </SelectItem>
              <SelectItem value="amount-desc">
                <div className="flex items-center gap-2">
                  <ArrowDown className="h-3 w-3" />
                  Amount (High)
                </div>
              </SelectItem>
              <SelectItem value="amount-asc">
                <div className="flex items-center gap-2">
                  <ArrowUp className="h-3 w-3" />
                  Amount (Low)
                </div>
              </SelectItem>
              <SelectItem value="title-asc">
                <div className="flex items-center gap-2">
                  <ArrowUp className="h-3 w-3" />
                  Title (A-Z)
                </div>
              </SelectItem>
              <SelectItem value="title-desc">
                <div className="flex items-center gap-2">
                  <ArrowDown className="h-3 w-3" />
                  Title (Z-A)
                </div>
              </SelectItem>
              <SelectItem value="state-asc">
                <div className="flex items-center gap-2">
                  <ArrowUp className="h-3 w-3" />
                  State (A-Z)
                </div>
              </SelectItem>
              <SelectItem value="status-asc">
                <div className="flex items-center gap-2">
                  <ArrowUp className="h-3 w-3" />
                  Status (A-Z)
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={handleExport} variant="outline" size="default" data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => window.print()} variant="outline" size="default" data-testid="print-button">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={() => setShowFilters(!showFilters)} variant="outline" size="default" data-testid="toggle-filters-button">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>
      </div>

      {/* Date Range Filter for Overview Cards */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 print:hidden">
        <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">Overview Period:</span>
        <div className="flex flex-wrap gap-1 bg-muted/50 p-1 rounded-lg w-full sm:w-auto">
          <Button
            size="sm"
            variant={dateRangeFilter === 'all' ? 'default' : 'ghost'}
            onClick={() => setDateRangeFilter('all')}
            className="h-8 text-xs flex-1 sm:flex-none"
            data-testid="date-filter-all"
          >
            All Time
          </Button>
          <Button
            size="sm"
            variant={dateRangeFilter === 'year' ? 'default' : 'ghost'}
            onClick={() => setDateRangeFilter('year')}
            className="h-8 text-xs flex-1 sm:flex-none"
            data-testid="date-filter-year"
          >
            This Year
          </Button>
          <Button
            size="sm"
            variant={dateRangeFilter === 'quarter' ? 'default' : 'ghost'}
            onClick={() => setDateRangeFilter('quarter')}
            className="h-8 text-xs flex-1 sm:flex-none"
            data-testid="date-filter-quarter"
          >
            Quarter
          </Button>
          <Button
            size="sm"
            variant={dateRangeFilter === 'month' ? 'default' : 'ghost'}
            onClick={() => setDateRangeFilter('month')}
            className="h-8 text-xs flex-1 sm:flex-none"
            data-testid="date-filter-month"
          >
            Month
          </Button>
        </div>
      </div>

      {/* Expense Prompts Banner */}
      {expensePrompts.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20" data-testid="expense-prompts-banner">
          <Bell className="h-5 w-5 text-amber-600" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-amber-900 dark:text-amber-100">
                  {expensePrompts.length} Pending Expense {expensePrompts.length === 1 ? 'Prompt' : 'Prompts'}
                </h4>
                <AlertDescription className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  Review and complete expense records from recent compliance tasks
                </AlertDescription>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setPromptsExpanded(!promptsExpanded)}
                className="text-amber-700 hover:text-white hover:bg-amber-600"
              >
                {promptsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>

            {promptsExpanded && (
              <div className="mt-4 space-y-2">
                {expensePrompts.map(prompt => (
                  <div 
                    key={prompt.prompt_id} 
                    className="flex items-start gap-3 p-3 bg-white dark:bg-slate-900 rounded-md border border-amber-200"
                    data-testid={`expense-prompt-${prompt.prompt_id}`}
                  >
                    <DollarSign className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{prompt.suggested_title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {prompt.suggested_state && `${prompt.suggested_state} • `}
                        {formatCurrency(prompt.suggested_amount || 0)}
                        {prompt.suggested_category && ` • ${prompt.suggested_category.replace(/_/g, ' ')}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleCompletePrompt(prompt)}
                        data-testid={`complete-prompt-${prompt.prompt_id}`}
                      >
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                        Complete
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDismissPrompt(prompt.prompt_id)}
                        data-testid={`dismiss-prompt-${prompt.prompt_id}`}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Alert>
      )}

      {/* COMPLIANCE FINANCIAL CONTROL PANEL - State Matrix Integrated */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 print:hidden" data-testid="expense-overview-cards">
        
        {/* A. TOTAL COMPLIANCE SPEND */}
        <Card 
          data-testid="card-total-spend" 
          className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-blue-500"
          onClick={() => {
            // Reset all filters to show total spend view
            setFilters({ ...filters, category: 'all', status: 'paid,submitted' });
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Total Compliance Spend
            </CardTitle>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-muted animate-pulse rounded" />
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold tabular-nums text-blue-700 dark:text-blue-400">
                  {formatCurrency(overviewStats?.cards?.total_spend?.total_spend || 0)}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted-foreground">
                    {overviewStats?.cards?.total_spend?.transaction_count || 0} transactions
                  </p>
                  {overviewStats?.cards?.total_spend?.change_vs_prev_period !== 0 && (
                    <span className={`text-xs font-medium ${overviewStats?.cards?.total_spend?.change_vs_prev_period > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {overviewStats?.cards?.total_spend?.change_vs_prev_period > 0 ? '+' : ''}
                      {overviewStats?.cards?.total_spend?.change_vs_prev_period}%
                    </span>
                  )}
                </div>
                {overviewStats?.cards?.total_spend?.largest_expense && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    Largest: {formatCurrency(overviewStats.cards.total_spend.largest_expense.amount)} ({overviewStats.cards.total_spend.largest_expense.state})
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* B. UPCOMING FILINGS (State Matrix SSOT) */}
        <Card 
          data-testid="card-upcoming-filings" 
          className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-amber-500"
          onClick={() => {
            setFilters({ ...filters, category: 'state_filing_fee', status: 'all' });
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Upcoming Filings
            </CardTitle>
            <FileBarChart className="h-5 w-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-muted animate-pulse rounded" />
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold tabular-nums text-amber-700 dark:text-amber-400">
                  {overviewStats?.cards?.upcoming_filings?.count_30_days || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Next 30 days • {overviewStats?.cards?.upcoming_filings?.count_60_days || 0} in 60 days
                </p>
                {overviewStats?.cards?.upcoming_filings?.nearest_filing && (
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mt-1">
                    Next: {overviewStats.cards.upcoming_filings.nearest_filing.state} in {overviewStats.cards.upcoming_filings.nearest_filing.days_until}d
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Projected: {formatCurrency(overviewStats?.cards?.upcoming_filings?.projected_cost || 0)}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* C. LICENSE RISK STATUS (State Matrix SSOT) */}
        <Card 
          data-testid="card-license-risk" 
          className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${
            overviewStats?.cards?.license_risk?.risk_level === 'critical' ? 'border-l-red-500' :
            overviewStats?.cards?.license_risk?.risk_level === 'high' ? 'border-l-orange-500' :
            overviewStats?.cards?.license_risk?.risk_level === 'medium' ? 'border-l-yellow-500' :
            'border-l-green-500'
          }`}
          onClick={() => {
            setFilters({ ...filters, category: 'license_application_fee,license_renewal_fee', status: 'all' });
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              License Risk
            </CardTitle>
            <Award className={`h-5 w-5 ${
              overviewStats?.cards?.license_risk?.risk_level === 'critical' ? 'text-red-600' :
              overviewStats?.cards?.license_risk?.risk_level === 'high' ? 'text-orange-600' :
              overviewStats?.cards?.license_risk?.risk_level === 'medium' ? 'text-yellow-600' :
              'text-green-600'
            }`} />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-muted animate-pulse rounded" />
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <div className={`text-2xl font-bold tabular-nums ${
                    overviewStats?.cards?.license_risk?.overdue_count > 0 ? 'text-red-700 dark:text-red-400' :
                    overviewStats?.cards?.license_risk?.expiring_30_days > 0 ? 'text-orange-700 dark:text-orange-400' :
                    'text-green-700 dark:text-green-400'
                  }`}>
                    {overviewStats?.cards?.license_risk?.total_at_risk || 0}
                  </div>
                  <span className="text-xs text-muted-foreground uppercase font-semibold">
                    {overviewStats?.cards?.license_risk?.risk_level || 'low'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted-foreground">
                    {overviewStats?.cards?.license_risk?.overdue_count || 0} overdue
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {overviewStats?.cards?.license_risk?.expiring_30_days || 0} in 30d
                  </p>
                </div>
                {overviewStats?.cards?.license_risk?.nearest_license && (
                  <p className="text-xs font-medium mt-1 truncate" title={overviewStats.cards.license_risk.nearest_license.license_type}>
                    Next: {overviewStats.cards.license_risk.nearest_license.state} in {overviewStats.cards.license_risk.nearest_license.days_until}d
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* D. ENTITY & REGISTRATION HEALTH (State Matrix SSOT) */}
        <Card 
          data-testid="card-entity-registration" 
          className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-indigo-500"
          onClick={() => {
            setFilters({ ...filters, category: 'dba_registration_fee,foreign_registration_fee', status: 'all' });
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Registrations
            </CardTitle>
            <Building className="h-5 w-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-muted animate-pulse rounded" />
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-400">
                  {overviewStats?.cards?.entity_registration?.active_count || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Active DBAs & Registrations
                </p>
                {(overviewStats?.cards?.entity_registration?.expiring_30_days > 0 || overviewStats?.cards?.entity_registration?.expiring_60_days > 0) && (
                  <p className="text-xs font-medium text-orange-600 dark:text-orange-400 mt-1">
                    {overviewStats.cards.entity_registration.expiring_30_days || 0} expiring soon
                  </p>
                )}
                {overviewStats?.cards?.entity_registration?.nearest_renewal && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    Next: {overviewStats.cards.entity_registration.nearest_renewal.state} in {overviewStats.cards.entity_registration.nearest_renewal.days_until}d
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* E. ACTION REQUIRED (Merged State Matrix + Expense Urgency) */}
        <Card 
          data-testid="card-action-required" 
          className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${
            overviewStats?.cards?.action_required?.urgency_level === 'critical' ? 'border-l-red-600' :
            overviewStats?.cards?.action_required?.urgency_level === 'high' ? 'border-l-orange-600' :
            'border-l-slate-500'
          }`}
          onClick={() => {
            setFilters({ ...filters, status: 'pending,overdue,draft', category: 'all' });
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Action Required
            </CardTitle>
            <AlertTriangle className={`h-5 w-5 ${
              overviewStats?.cards?.action_required?.urgency_level === 'critical' ? 'text-red-600' :
              overviewStats?.cards?.action_required?.urgency_level === 'high' ? 'text-orange-600' :
              'text-slate-600'
            }`} />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-muted animate-pulse rounded" />
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <div className={`text-2xl font-bold tabular-nums ${
                    overviewStats?.cards?.action_required?.urgency_level === 'critical' ? 'text-red-700 dark:text-red-400' :
                    overviewStats?.cards?.action_required?.urgency_level === 'high' ? 'text-orange-700 dark:text-orange-400' :
                    'text-slate-700 dark:text-slate-400'
                  }`}>
                    {overviewStats?.cards?.action_required?.total_actions || 0}
                  </div>
                  <span className="text-xs text-muted-foreground uppercase font-semibold">
                    {overviewStats?.cards?.action_required?.urgency_level || 'normal'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span>{overviewStats?.cards?.action_required?.critical_states || 0} critical states</span>
                  <span>{overviewStats?.cards?.action_required?.overdue_tasks || 0} overdue tasks</span>
                  <span>{overviewStats?.cards?.action_required?.unpaid_expenses || 0} unpaid</span>
                  <span>{overviewStats?.cards?.action_required?.draft_prompts || 0} prompts</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Advanced Filter Bar */}
      {showFilters && (
        <Card className="print:hidden">
          <CardHeader>
            <CardTitle className="text-lg">Advanced Filters</CardTitle>
            <CardDescription>Filter expenses by multiple criteria for detailed analysis</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Row 1: Search and States */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Keyword Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search notes, vendor, invoice..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>States ({filters.states.length} selected)</Label>
                <div className="border rounded-md p-2 max-h-32 overflow-y-auto">
                  <div className="grid grid-cols-3 gap-1">
                    {US_STATES.slice(0, 12).map(state => (
                      <label key={state} className="flex items-center space-x-1 text-xs cursor-pointer hover:bg-accent p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filters.states.includes(state)}
                          onChange={() => toggleState(state)}
                          className="rounded"
                        />
                        <span>{state.substring(0, 2)}</span>
                      </label>
                    ))}
                  </div>
                  <details className="mt-1">
                    <summary className="text-xs text-blue-600 cursor-pointer">Show all {US_STATES.length} states</summary>
                    <div className="grid grid-cols-3 gap-1 mt-1">
                      {US_STATES.slice(12).map(state => (
                        <label key={state} className="flex items-center space-x-1 text-xs cursor-pointer hover:bg-accent p-1 rounded">
                          <input
                            type="checkbox"
                            checked={filters.states.includes(state)}
                            onChange={() => toggleState(state)}
                            className="rounded"
                          />
                          <span>{state.substring(0, 2)}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                </div>
              </div>
            </div>

            {/* Row 2: Category and Status */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">All Categories</SelectItem>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">State Filing</div>
                    {CATEGORIES.slice(0, 7).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">Licensing & Renewal</div>
                    {CATEGORIES.slice(7, 14).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">DBA & Name Registration</div>
                    {CATEGORIES.slice(14, 20).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">Other</div>
                    {CATEGORIES.slice(20).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Min Amount</Label>
                <Input
                  type="number"
                  placeholder="$0"
                  value={filters.min_amount}
                  onChange={(e) => setFilters({ ...filters, min_amount: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Max Amount</Label>
                <Input
                  type="number"
                  placeholder="No limit"
                  value={filters.max_amount}
                  onChange={(e) => setFilters({ ...filters, max_amount: e.target.value })}
                />
              </div>
            </div>

            {/* Row 3: Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  placeholder="mm/dd/yyyy"
                />
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  placeholder="mm/dd/yyyy"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={applyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button onClick={resetFilters} variant="outline" className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>
          Showing {expenses.length} of {pagination.total} expenses
          {filters.search && ` matching "${filters.search}"`}
          {filters.states.length > 0 && ` in ${filters.states.length} state(s)`}
        </div>
        <div>
          Page {pagination.page} of {pagination.pages}
        </div>
      </div>

      {/* Expense Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr className="border-b">
                  <th 
                    onClick={() => handleSort('payment_date')}
                    className="text-left p-3 font-semibold text-sm cursor-pointer hover:bg-accent group"
                  >
                    <div className="flex items-center gap-1">
                      Date
                      <SortIndicator column="payment_date" />
                    </div>
                  </th>
                  <th className="text-left p-3 font-semibold text-sm">Title</th>
                  <th 
                    onClick={() => handleSort('state')}
                    className="text-left p-3 font-semibold text-sm cursor-pointer hover:bg-accent group"
                  >
                    <div className="flex items-center gap-1">
                      State
                      <SortIndicator column="state" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('category')}
                    className="text-left p-3 font-semibold text-sm cursor-pointer hover:bg-accent group"
                  >
                    <div className="flex items-center gap-1">
                      Category
                      <SortIndicator column="category" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('amount')}
                    className="text-right p-3 font-semibold text-sm cursor-pointer hover:bg-accent group"
                  >
                    <div className="flex items-center justify-end gap-1">
                      Amount
                      <SortIndicator column="amount" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('status')}
                    className="text-center p-3 font-semibold text-sm cursor-pointer hover:bg-accent group"
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status
                      <SortIndicator column="status" />
                    </div>
                  </th>
                  <th className="text-center p-3 font-semibold text-sm">Invoice</th>
                  <th className="text-center p-3 font-semibold text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center p-12 text-muted-foreground">
                      Loading expenses...
                    </td>
                  </tr>
                ) : expenses.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-12 text-muted-foreground">
                      No expenses found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  expenses.map((expense) => (
                    <tr 
                      key={expense.expense_id}
                      onClick={() => {
                        setSelectedExpense(expense);
                        setDetailDialogOpen(true);
                      }}
                      className={`group border-b cursor-pointer hover:bg-accent/50 transition-colors ${
                        expense.amount > 1000 ? 'bg-yellow-50/30' : ''
                      }`}
                    >
                      <td className="p-3 text-sm">{formatDate(expense.payment_date)}</td>
                      <td className="p-3 text-sm font-medium">{expense.title}</td>
                      <td className="p-3 text-sm">{expense.state}</td>
                      <td className="p-3 text-sm">
                        <Badge variant="outline">{expense.category}</Badge>
                      </td>
                      <td className="p-3 text-sm text-right font-semibold">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={getStatusColor(expense.status)}>
                          {expense.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        {expense.invoice_filename ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPreviewInvoice(expense)}
                              title="View invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleInvoiceDelete(expense.expense_id)}
                              title="Delete invoice"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleInvoiceUpload(expense.expense_id, e.target.files[0]);
                                }
                              }}
                              className="hidden"
                              id={`invoice-upload-${expense.expense_id}`}
                            />
                            <label htmlFor={`invoice-upload-${expense.expense_id}`}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => document.getElementById(`invoice-upload-${expense.expense_id}`).click()}
                                disabled={uploadingInvoice === expense.expense_id}
                              >
                                {uploadingInvoice === expense.expense_id ? (
                                  <>Uploading...</>
                                ) : (
                                  <>
                                    <Upload className="h-4 w-4 mr-1" />
                                    Upload
                                  </>
                                )}
                              </Button>
                            </label>
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label>Per page:</Label>
            <Select 
              value={String(filters.limit)} 
              onValueChange={(v) => setFilters({ ...filters, limit: parseInt(v), page: 1 })}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm px-4">
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page >= pagination.pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Expense Details</DialogTitle>
            <DialogDescription>Complete expense record information</DialogDescription>
          </DialogHeader>
          {selectedExpense && (
            <div className="space-y-6">
              {/* Title and Amount Row */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">Title</Label>
                  <p className="font-semibold text-lg">{selectedExpense.title}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">Amount</Label>
                  <p className="font-semibold text-2xl">{formatCurrency(selectedExpense.amount)}</p>
                </div>
              </div>

              {/* State and Category Row */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">State</Label>
                  <p className="text-base">{selectedExpense.state}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">Category</Label>
                  <p className="text-base">{selectedExpense.category}</p>
                </div>
              </div>

              {/* Payment Date and Status Row */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">Payment Date</Label>
                  <p className="text-base">{formatDate(selectedExpense.payment_date)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-sm">Status</Label>
                  <div>
                    <Badge className={getStatusColor(selectedExpense.status)}>
                      {selectedExpense.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Vendor and Reference Number (if present) */}
              {(selectedExpense.vendor || selectedExpense.reference_number) && (
                <div className="grid grid-cols-2 gap-6">
                  {selectedExpense.vendor && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-sm">Vendor</Label>
                      <p className="text-base">{selectedExpense.vendor}</p>
                    </div>
                  )}
                  {selectedExpense.reference_number && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-sm">Reference #</Label>
                      <p className="text-base">{selectedExpense.reference_number}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {selectedExpense.description && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-muted-foreground text-sm">Description</Label>
                  <p className="text-base leading-relaxed">{selectedExpense.description}</p>
                </div>
              )}

              {/* Notes */}
              {selectedExpense.notes && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Notes</Label>
                  <p className="text-base leading-relaxed">{selectedExpense.notes}</p>
                </div>
              )}

              {/* Linked Compliance Objects */}
              {(selectedExpense.entity_id || selectedExpense.license_id || selectedExpense.dba_id || selectedExpense.task_id) && (
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-muted-foreground text-sm">Linked Compliance Records</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {selectedExpense.entity_id && (
                      <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">Entity</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{selectedExpense.entity_id}</span>
                      </div>
                    )}
                    {selectedExpense.license_id && (
                      <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium">License</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{selectedExpense.license_id}</span>
                      </div>
                    )}
                    {selectedExpense.dba_id && (
                      <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950/20 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          <span className="text-sm font-medium">DBA / Trade Name</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{selectedExpense.dba_id}</span>
                      </div>
                    )}
                    {selectedExpense.task_id && (
                      <div className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-950/20 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileBarChart className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-medium">Related Task</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{selectedExpense.task_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Attachment */}
              {selectedExpense.invoice_filename && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-muted-foreground text-sm">Invoice Attachment</Label>
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md">
                    <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm flex-1 truncate">{selectedExpense.invoice_filename}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const url = api.downloadInvoice(selectedExpense.expense_id, selectedExpense.invoice_filename);
                        window.open(url, '_blank');
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => handleEditExpense(selectedExpense)} data-testid="edit-expense-button">
              <Edit className="h-4 w-4 mr-2" />
              Edit Expense
            </Button>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={!!previewInvoice} onOpenChange={() => setPreviewInvoice(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
            <DialogDescription>{previewInvoice?.title}</DialogDescription>
          </DialogHeader>
          {previewInvoice && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium">{previewInvoice.invoice_filename}</span>
              </div>
              
              {previewInvoice.invoice_content_type?.includes('pdf') ? (
                <div className="w-full h-[600px] border rounded">
                  <iframe
                    src={api.downloadInvoice(previewInvoice.expense_id, previewInvoice.invoice_filename)}
                    className="w-full h-full rounded"
                    title="Invoice Preview"
                  />
                </div>
              ) : (
                <div className="w-full border rounded p-4 flex items-center justify-center">
                  <img
                    src={api.downloadInvoice(previewInvoice.expense_id, previewInvoice.invoice_filename)}
                    alt="Invoice"
                    className="max-w-full max-h-[600px] object-contain"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between items-center sm:justify-between gap-3">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  const url = api.downloadInvoice(previewInvoice.expense_id, previewInvoice.invoice_filename);
                  window.open(url, '_blank');
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleInvoiceDelete(previewInvoice.expense_id);
                  setPreviewInvoice(null);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Invoice
              </Button>
            </div>
            <Button onClick={() => setPreviewInvoice(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Expense Dialog */}
      <AddExpenseDialog
        open={addExpenseOpen}
        onClose={() => {
          setAddExpenseOpen(false);
          setEditingExpense(null);
        }}
        onSuccess={handleExpenseSuccess}
        expense={editingExpense}
      />
    </div>
  );
};

export default ExpenseTrackerEnhanced;
